import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { setErrorOn, setLoadingOn, unsetLoadingAndErrorOn } from 'ish-core/utils/ngrx-creators';

import { Wishlist } from '../../models/wishlist/wishlist.model';

import {
  addBasketToNewWishList,
  addBasketToNewWishListFail,
  addBasketToNewWishlistSuccess,
  addProductToWishlistSuccess,
  createWishlist,
  createWishlistFail,
  createWishlistSuccess,
  deleteWishlist,
  deleteWishlistFail,
  deleteWishlistSuccess,
  loadWishlists,
  loadWishlistsFail,
  loadWishlistsSuccess,
  moveItemToWishlist,
  removeItemFromWishlist,
  removeItemFromWishlistSuccess,
  selectWishlist,
  updateWishlist,
  updateWishlistFail,
  updateWishlistSuccess,
  wishlistActions,
  wishlistApiActions,
} from './wishlist.actions';

export interface WishlistState extends EntityState<Wishlist> {
  loading: boolean;
  selected: string;
  error: HttpError;
  sharedWishlist: Wishlist;
}

export const wishlistsAdapter = createEntityAdapter<Wishlist>({
  selectId: wishlist => wishlist.id,
});

export const initialState: WishlistState = wishlistsAdapter.getInitialState({
  loading: false,
  selected: undefined,
  error: undefined,
  sharedWishlist: undefined,
});

export const wishlistReducer = createReducer(
  initialState,
  setLoadingOn(
    loadWishlists,
    wishlistActions.loadSharedWishlist,
    createWishlist,
    deleteWishlist,
    updateWishlist,
    addBasketToNewWishList, //Dias Customization
    removeItemFromWishlist,
    moveItemToWishlist
  ),
  setErrorOn(
    loadWishlistsFail,
    wishlistApiActions.loadSharedWishlistFail,
    deleteWishlistFail,
    createWishlistFail,
    updateWishlistFail,
    wishlistApiActions.loadSharedWishlistFail,
    wishlistApiActions.shareWishlistFail,
    wishlistApiActions.unshareWishlistFail,
    addBasketToNewWishListFail
  ),
  unsetLoadingAndErrorOn(
    updateWishlistSuccess,
    addProductToWishlistSuccess,
    addBasketToNewWishlistSuccess, //Dias Customization
    removeItemFromWishlistSuccess,
    createWishlistSuccess,
    loadWishlistsSuccess,
    wishlistApiActions.loadSharedWishlistSuccess,
    deleteWishlistSuccess
  ),
  on(
    loadWishlistsFail,
    deleteWishlistFail,
    createWishlistFail,
    updateWishlistFail,
    addBasketToNewWishListFail, //Dias Customization
    (state: WishlistState): WishlistState => ({
      ...state,
      selected: undefined as string,
    })
  ),
  on(loadWishlistsSuccess, (state: WishlistState, action) => {
    const { wishlists } = action.payload;
    return wishlistsAdapter.setAll(wishlists, state);
  }),
  on(
    updateWishlistSuccess,
    wishlistApiActions.loadSharedWishlistSuccess,
    addProductToWishlistSuccess,
    addProductToWishlistSuccess, //Dias Customization
    removeItemFromWishlistSuccess,
    createWishlistSuccess,
    (state, action) => {
      const { wishlist } = action.payload;

      return wishlistsAdapter.upsertOne(wishlist, state);
    }
  ),
  on(deleteWishlistSuccess, (state, action) => {
    const { wishlistId } = action.payload;
    return wishlistsAdapter.removeOne(wishlistId, state);
  }),
  on(selectWishlist, wishlistActions.loadSharedWishlist, (state, action): WishlistState => {
    const { wishlistId: id } = action.payload;
    return {
      ...state,
      selected: id,
    };
  }),
  on(wishlistApiActions.shareWishlistSuccess, (state, action): WishlistState => {
    const wishlistSharingResponse = action.payload.wishlistSharingResponse;
    const wishlistId = wishlistSharingResponse.wishlistId;

    const updatedWishlist: Wishlist = {
      ...state.entities[wishlistId],
      shared: true,
    };

    return wishlistsAdapter.upsertOne(updatedWishlist, state);
  }),
  on(wishlistApiActions.unshareWishlistSuccess, (state, action): WishlistState => {
    const wishlistId = action.payload.wishlistId;

    const updatedWishlist: Wishlist = {
      ...state.entities[wishlistId],
      shared: false,
    };

    return wishlistsAdapter.upsertOne(updatedWishlist, state);
  }),
  on(
    wishlistApiActions.loadSharedWishlistSuccess,
    (state, action): WishlistState => ({
      ...state,
      sharedWishlist: action.payload.wishlist,
    })
  )
);
